import React, { useState } from "react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import opentype from "opentype.js";
import {
  CopyIcon,
  CopySuccessIcon,
  DownloadIcon,
  EditIcon,
  FileIcon,
  ResetIcon,
  TrashIcon,
} from "./Icons";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const ListItem = ({
  newFileName,
  fullName,
  downloadFile,
  index,
  handleRemoveFont,
}) => {
  const [copySuccess, setCopySuccess] = useState(false);

  const copyFullName = (fullName) => {
    navigator.clipboard.writeText(fullName);
    setCopySuccess(true);
    setTimeout(() => {
      setCopySuccess(false);
    }, 1000);
  };
  return (
    <li className="bg-gray-50 p-3 rounded">
      <p className="text-sm text-slate-600 mb-1">{newFileName}</p>
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500">Full Name: {fullName}</span>
        <div className="space-x-2 flex items-center">
          <button
            onClick={() => copyFullName(fullName)}
            className={classNames(
              "text-xs bg-slate-200 hover:bg-slate-300 font-bold py-1 px-2 rounded",
              copySuccess ? "text-green-500" : "text-slate-800"
            )}
          >
            {copySuccess ? <CopySuccessIcon /> : <CopyIcon />}
          </button>
          <button
            onClick={() => {
              downloadFile(newFileName, index);
            }}
            className="text-xs bg-slate-200 hover:bg-slate-300 text-slate-800 font-bold py-1 px-2 rounded"
          >
            <DownloadIcon />
          </button>
          <button
            onClick={() => handleRemoveFont(index)}
            className="text-xs bg-red-200 hover:bg-red-300 text-red-800 font-bold py-1 px-2 rounded"
          >
            <TrashIcon />
          </button>
        </div>
      </div>
    </li>
  );
};

function App() {
  const [files, setFiles] = useState([]);
  const [renamedFiles, setRenamedFiles] = useState([]);
  const [json, setJson] = useState(undefined);
  const [editingJson, setEditingJson] = useState(false);
  const [jsonCopySuccess, setJsonCopySuccess] = useState(false);

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);
    setFiles(selectedFiles);

    const renamedFilesPromises = selectedFiles.map((file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const arrayBuffer = e.target.result;
          try {
            const font = opentype.parse(arrayBuffer);
            const fullName = font.names.fullName.en;
            const extension = file.name.split(".").pop().toLowerCase();
            const newFileName = `${fullName}.${extension}`;
            resolve({ file, newFileName, fullName });
          } catch (error) {
            reject(error);
          }
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(file);
      });
    });

    Promise.all(renamedFilesPromises)
      .then((results) => {
        setRenamedFiles(results);
        updateJson(results);
      })
      .catch((error) => console.error("Error processing files:", error));
  };

  const updateJson = (results) => {
    const fontFamily = results.reduce((acc, { fullName }) => {
      const key = fullName.replace(/\s+/g, "_").toLowerCase();
      acc[key] = fullName;
      return acc;
    }, {});

    const json = JSON.stringify({ fontFamily }, null, 2);
    setJson(json);
  };

  const downloadZip = () => {
    const zip = new JSZip();
    renamedFiles.forEach(({ file, newFileName }) => {
      zip.file(newFileName, file);
    });
    zip.generateAsync({ type: "blob" }).then((content) => {
      saveAs(content, "renamed-fonts.zip");
    });
  };

  const downloadFile = (newFileName, index) => {
    const zip = new JSZip();
    zip.file(newFileName, files[index]);
    zip.generateAsync({ type: "blob" }).then((content) => {
      saveAs(content, newFileName);
    });
  };

  const copyJson = () => {
    navigator.clipboard.writeText(json);
    setJsonCopySuccess(true);
    setTimeout(() => {
      setJsonCopySuccess(false);
    }, 1000);
  };

  const handleNewFontFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const arrayBuffer = e.target.result;
        try {
          const font = opentype.parse(arrayBuffer);
          const fullName = font.names.fullName.en;
          const extension = file.name.split(".").pop().toLowerCase();
          const newFileName = `${fullName}.${extension}`;

          const newFont = {
            file,
            newFileName,
            fullName,
          };

          const updatedRenamedFiles = [...renamedFiles, newFont];
          setRenamedFiles(updatedRenamedFiles);
          updateJson(updatedRenamedFiles);
        } catch (error) {
          console.error("Error processing new font file:", error);
        }
      };
      reader.onerror = () =>
        console.error("Error reading new font file:", reader.error);
      reader.readAsArrayBuffer(file);
    }
  };

  const handleRemoveFont = (index) => {
    const updatedRenamedFiles = renamedFiles.filter((_, i) => i !== index);
    setRenamedFiles(updatedRenamedFiles);
    updateJson(updatedRenamedFiles);
  };

  const handleReset = () => {
    setFiles([]);
    setRenamedFiles([]);
    setJson(undefined);
    setEditingJson(false);
    setJsonCopySuccess(false);
  };

  return (
    <div className="bg-slate-900 min-h-screen overflow-y-auto">
      <div className="max-w-lg mx-auto mt-8 px-6 py-4 bg-white rounded-lg shadow-xl border border-slate-100">
        <h1 className="text-2xl font-bold mb-3 text-gray-800">Font Renamer</h1>
        <p className="text-sm text-gray-500">
          This tool uploads font files, renames them based on internal names,
          and downloads them individually or as a zip. It also generates a JSON
          mapping to avoid font name conflicts in React Native builds.
        </p>
        <div className="my-4 h-px bg-gray-200" />
        <div className="mb-4">
          <label
            htmlFor="file-input"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Select font files:
          </label>
          <input
            id="file-input"
            type="file"
            multiple
            onChange={handleFileChange}
            accept=".ttf"
            className="block w-full text-sm text-gray-500
            file:mr-4 file:py-2 file:px-4
            file:rounded-full file:border-0
            file:text-sm file:font-semibold
            file:bg-blue-50 file:text-blue-700
            hover:file:bg-blue-100"
          />
          <small className="text-xs text-gray-500 mt-2 inline-block">
            Only TrueType Font (.ttf) files are supported.
          </small>
        </div>
        <button
          onClick={downloadZip}
          disabled={renamedFiles.length === 0}
          className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded disabled:opacity-50 disabled:cursor-not-allowed mb-4 flex items-center justify-center space-x-2"
        >
          <FileIcon />
          <span>Download All Renamed Fonts</span>
        </button>
        {renamedFiles.length > 0 && (
          <>
            <ul className="space-y-2 mb-4">
              {renamedFiles.map(({ newFileName, fullName }, index) => (
                <ListItem
                  key={index}
                  downloadFile={downloadFile}
                  newFileName={newFileName}
                  fullName={fullName}
                  index={index}
                  handleRemoveFont={handleRemoveFont}
                />
              ))}
            </ul>
            <div className="mb-4">
              <label
                htmlFor="new-font-input"
                className="block text-sm font-medium text-gray-700 mb-2 border border-dashed border-gray-300 rounded-md p-2 text-center cursor-pointer hover:bg-gray-50 hover:border-gray-400"
              >
                + Add New Font
              </label>
              <input
                id="new-font-input"
                type="file"
                onChange={handleNewFontFileChange}
                accept=".ttf"
                className="hidden"
              />
            </div>
          </>
        )}
        {json && renamedFiles.length > 0 && (
          <div className="mt-6">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-lg font-bold text-gray-800">JSON Output</h2>
              <div className="flex space-x-2">
                <button
                  onClick={() => setEditingJson(!editingJson)}
                  className="bg-gray-700 hover:bg-gray-600 text-white text-xs font-bold py-1.5 px-2 rounded"
                >
                  {editingJson ? "Save JSON" : <EditIcon />}
                </button>
                <button
                  onClick={copyJson}
                  disabled={editingJson}
                  className={classNames(
                    "bg-gray-700 hover:bg-gray-600 font-bold py-1.5 px-2 rounded disabled:opacity-50 disabled:cursor-not-allowed",
                    jsonCopySuccess ? "text-green-500" : "text-white"
                  )}
                >
                  {jsonCopySuccess ? <CopySuccessIcon /> : <CopyIcon />}
                </button>
              </div>
            </div>
            <textarea
              className="w-full p-2 border border-gray-300 rounded-md mb-2 text-sm"
              rows="10"
              value={json}
              onChange={(e) => setJson(e.target.value)}
              disabled={!editingJson}
            ></textarea>
          </div>
        )}
        {renamedFiles.length > 0 && (
          <div className="flex justify-end">
            <button
              onClick={handleReset}
              className="bg-red-100 hover:bg-red-200 text-red-600 py-1.5 px-2 rounded inline-flex items-center space-x-2 text-sm"
            >
              <ResetIcon />
              <span>Reset</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
